import { cn } from "@-/com.npmjs.cn";
import { View } from "@-/com.npmjs.react-native";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import { RootContext } from "@-/dev.improve.haniverse.routes._context";
import { Skeleton } from "@-/dev.improve.haniverse.components.ui.skeleton";
import { useState } from "react";
import {
  computeTileSize,
  computeColumnCount,
  DEFAULT_COLUMN_COUNT,
  GRID_HORIZONTAL_PADDING,
  MAX_TILE_SIZE,
} from "#card-details-tile-grid-metrics";

/**
 * Loading placeholder for a non-scrollable {@link CardDetailsTileList}: the same padding, gutters,
 * column count and square tile size, with `tileCount` tiles (the last row left partially empty
 * exactly as the loaded grid leaves it) capped at `maxRowCount` rows.
 *
 * Pass `columnCount` when the loaded grid uses a fixed column count (e.g. levels preview always
 * uses 4) so the skeleton does not grow extra columns on wide viewports.
 */
export function CardDetailsTileListSkeleton({
  columnCount: columnCountOverride,
  maxRowCount = 4,
  padding = GRID_HORIZONTAL_PADDING,
  tileCount,
}: {
  columnCount?: number;
  maxRowCount?: number;
  padding?: number;
  tileCount: number;
}) {
  const [windowWidth] = useContextSelectors(RootContext, (c) => c.windowDimensions.width);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  const widthForLayout = containerWidth ?? windowWidth;
  const columnCount =
    columnCountOverride ??
    computeColumnCount(widthForLayout, DEFAULT_COLUMN_COUNT, MAX_TILE_SIZE, padding);
  const tileSize = computeTileSize(widthForLayout, columnCount, padding);
  const rowCount = Math.min(maxRowCount, Math.ceil(tileCount / columnCount));

  return (
    <View
      onLayout={({ nativeEvent: { layout } }) => {
        if (layout.width > 0 && layout.width !== containerWidth) {
          setContainerWidth(layout.width);
        }
      }}
      style={{ padding }}
      testID="card-details-tile-list-skeleton"
    >
      {Array.from({ length: rowCount }, (_, row) => (
        <View className={cn("flex-row gap-1", row < rowCount - 1 && "pb-1")} key={row}>
          {Array.from(
            { length: Math.min(columnCount, tileCount - row * columnCount) },
            (_, column) => (
              <Skeleton key={column} style={{ height: tileSize, width: tileSize }} />
            ),
          )}
        </View>
      ))}
    </View>
  );
}
